/* THE NEW PERSON'S CARD ON HOME (Jay task #1160, 02/11: "At Google signup, the person picks their Entity and Team. The account
   is active at once with User. The team maps to a requested role … Home shows 'Role approval pending' until approved").
   Nothing shows for anybody else: the card asks /api/signup/me and draws only a sign-up that is not finished, a role that
   is waiting, or a decision the person has not seen yet (remembered per request in this browser). */
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UsersRound, Hourglass, CheckCircle2, XCircle } from 'lucide-react';
import { BACKEND_URL } from './backendConfig';
import './SPHub.css';
import './SignupCard.css';

const API = `${BACKEND_URL}/api/signup`;
const seenKey = (id) => `signupSeen:${id}`;

export default function SignupCard() {
  const { t } = useTranslation();
  const [d, setD] = useState(null);
  const [entity, setEntity] = useState('');
  const [team, setTeam] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [seen, setSeen] = useState(false);
  const load = () => fetch(`${API}/me`, { credentials: 'include' }).then((r) => (r.ok ? r.json() : null)).then((x) => {
    setD(x);
    try { if (x?.request) setSeen(localStorage.getItem(seenKey(x.request.id)) === x.request.status); } catch (e) { /* private window */ }
  }).catch(() => {});
  useEffect(() => { load(); }, []);
  const teams = useMemo(() => (d?.teams || []).filter((x) => x.entityKey === entity), [d, entity]);
  const picked = teams.find((x) => String(x.id) === String(team));
  const rq = d?.request;
  if (!rq) return null;

  if (rq.status === 'needs_team') {
    const save = async () => {
      setBusy(true); setErr('');
      try {
        const r = await fetch(`${API}/team`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ entityKey: entity, teamKey: team || null }) });
        const j = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`);
        await load();
      } catch (e) { setErr(e.message); } finally { setBusy(false); }
    };
    return (
      <section className="su-card su-welcome" data-su-card="welcome" aria-labelledby="su-welcome-h">
        <div className="su-card-icon"><UsersRound size={22} aria-hidden="true" /></div>
        <div className="su-card-body">
          <h3 id="su-welcome-h">{t('xSignup.welcomeT')}</h3>
          <p>{t('xSignup.welcomeB')}</p>
          <div className="su-form">
            <label><span>{t('xSignup.entity')}</span>
              <select value={entity} onChange={(e) => { setEntity(e.target.value); setTeam(''); }} data-su-entity>
                <option value="">{t('xSignup.pick')}</option>
                {(d.entities || []).map((x) => <option key={x.key} value={x.key}>{x.key} · {x.name}</option>)}
              </select>
            </label>
            {teams.length > 0 && (
              <label><span>{t('xSignup.team')}</span>
                <select value={team} onChange={(e) => setTeam(e.target.value)} data-su-team>
                  <option value="">{t('xSignup.pick')}</option>
                  {teams.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
                </select>
              </label>
            )}
            <button type="button" className="sp-btn primary" disabled={busy || !entity || (teams.length > 0 && !team)} onClick={save} data-su-save>
              {busy ? t('xSignup.saving') : t('xSignup.save')}
            </button>
          </div>
          {picked && <p className="su-asks">{picked.role ? t('xSignup.asks', { role: picked.roleName }) : t('xSignup.asksNone')}</p>}
          {err && <p className="sp-error">{err}</p>}
        </div>
      </section>
    );
  }
  if (rq.status === 'pending') {
    return (
      <section className="su-card su-pending" data-su-card="pending" role="status">
        <div className="su-card-icon"><Hourglass size={20} aria-hidden="true" /></div>
        <div className="su-card-body">
          <h3>{t('xSignup.pendingT')}</h3>
          <p>{t('xSignup.pendingB', { role: rq.requestedRoleName || rq.requestedRole, team: [rq.entityKey, rq.team].filter(Boolean).join(' · ') })}</p>
        </div>
      </section>
    );
  }
  // a decision the person has not acknowledged yet (a team that asked for nothing beyond User shows nothing)
  if (seen || (rq.status === 'approved' && (!rq.decidedRole || rq.decidedRole === 'User'))) return null;
  const ok = rq.status === 'approved';
  const dismiss = () => { try { localStorage.setItem(seenKey(rq.id), rq.status); } catch (e) { /* ignore */ } setSeen(true); };
  return (
    <section className={`su-card ${ok ? 'su-ok' : 'su-no'}`} data-su-card={rq.status} role="status">
      <div className="su-card-icon">{ok ? <CheckCircle2 size={20} aria-hidden="true" /> : <XCircle size={20} aria-hidden="true" />}</div>
      <div className="su-card-body">
        <h3>{ok ? t('xSignup.approvedT', { role: rq.decidedRoleName || rq.decidedRole }) : t('xSignup.rejectedT')}</h3>
        {!ok && rq.note && <p>{rq.note}</p>}
      </div>
      <button type="button" className="sp-btn" onClick={dismiss}>{t('xSignup.dismiss')}</button>
    </section>
  );
}
